import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { CustomEventName, useCustomEvent } from "@/modules/custom-event";
import { useAppStore } from "@/store";
import { AudioRecordingObservable } from "@/store/audio-recordings";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export const useVoiceIndicatorBehavior = ({
  noteId,
  audioRecording,
  isSuggestionActive,
}: {
  noteId: string;
  audioRecording?: AudioRecordingObservable | null;
  isSuggestionActive: boolean;
}) => {
  const { store } = useAppStore();

  const [isOpen, setIsOpen] = useState(false);
  useCustomEvent(CustomEventName.ExpandVoicePopover, ({ noteId: eventNoteId }) => {
    if (eventNoteId === noteId) {
      setIsOpen(true);
    }
  });

  /**
   * We use this to coordinate with the server:
   * - Once we have received an associated suggestion from the server, we can mark the recording as completed.
   *
   * Note that this is somewhat unnecessary; as we expect the server to update the status immediately after
   * the suggestion is created.
   *
   * This is just to ensure there is no flickering in the ui.
   * (This could be improved by ensuring it is a voice suggestion, etc.)
   */
  useEffect(() => {
    const audioRecordingId = audioRecording?.id;
    if (!audioRecordingId) return;
    if (!audioRecording?.isAwaitingTransformRemoteUpdate) return;
    if (!isSuggestionActive) return;

    audioRecording.setAcknowledgedTransformRemoteUpdate();
    trackEvent(TrackedEvent.VoiceSessionSuggestedEditsView);

    // Auto-open the voice popover when suggestion arrives
    setIsOpen(true);

    const asyncLogic = async () => {
      await store.dexie.repositories.audioRecordings.markAsProcessingCompleted({
        id: audioRecordingId,
      });
    };

    asyncLogic();
  }, [
    isSuggestionActive,
    noteId,
    audioRecording,
    audioRecording?.id,
    audioRecording?.isAwaitingTransformRemoteUpdate,
    store.dexie.repositories.audioRecordings,
  ]);

  useLayoutEffect(() => {
    if (audioRecording?.isUploading || isSuggestionActive) {
      setIsOpen(true);
    }
  }, [audioRecording?.isUploading, isSuggestionActive]);

  const ref = useRef<HTMLButtonElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const handleRequestClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // Use requestAnimationFrame to ensure the popover is rendered before focusing
    requestAnimationFrame(() => {
      if (popoverContentRef.current) {
        // Focus the popover content to enable outside click detection
        popoverContentRef.current.focus();
      }
    });
  }, [isOpen]);

  return {
    handleRequestClose,
    popoverContentRef,
    ref,
    setIsOpen,
    isOpen,
  };
};
